import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import {
  ButtonContainer,
  Form,
  SpacerSizeTypes,
  TextLevel,
  useStore,
  Spacer,
  Input,
} from '@envision/ev-ui-component-library'
import api from 'api/api'
import { TYPE_SUCCESS } from 'components/Toast'
import { openToast } from 'store/toast'
import { Button } from 'components'
import MessageBox, { MessageBoxType } from 'components/MessageBox'
import { ButtonType } from 'typeScript/types/ButtonTypes'
import { TokenErrorCode, isWarningTokenError } from 'constants/tokenErrorCodes'

interface FormMessage {
  text: string
  type: MessageBoxType
}

const DEBOUNCE_DELAY_MS = 1000

interface AddExternalRfidFormProps {
  readonly onSuccess: () => void
}

interface ShellTokenResponse {
  readonly data: {
    readonly visualNumber: string
    readonly status: string
  }
  readonly status: number
}

function AddExternalRfidForm({ onSuccess }: AddExternalRfidFormProps) {
  const { t } = useTranslation(['rfid', 'toast', 'errorAPI'])
  const dispatch = useDispatch()
  const [eventEmitter] = useStore.eventEmitter()
  const [isChecking, setIsChecking] = useState(false)
  const [isCardValid, setIsCardValid] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formMessage, setFormMessage] = useState<FormMessage | null>(null)
  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      visualNumber: '',
    },
  })
  const { watch, clearErrors, reset } = formMethods
  const visualNumber = watch('visualNumber') as string

  const getTokenErrorInfo = useCallback(
    (error: any): FormMessage => {
      const code = error?.response?.data?.code

      if (code && Object.values(TokenErrorCode).includes(code)) {
        return {
          text: t(`errorAPI:${code}`),
          type: isWarningTokenError(code) ? 'warning' : 'error',
        }
      }
      return {
        text: t('external_rfid_add_external_not_found'),
        type: 'error',
      }
    },
    [t]
  )

  const isUniversCard = useCallback((number: string): boolean => {
    return number.trim().toUpperCase().startsWith('ENOS')
  }, [])

  const checkCardExists = useCallback(
    async (number: string): Promise<void> => {
      if (!number || number.trim().length < 3) {
        setIsCardValid(false)
        setFormMessage(null)
        return
      }

      // Check if it's a Univers card (starts with EnOS)
      if (isUniversCard(number)) {
        setIsCardValid(false)
        setFormMessage({
          text: t('external_rfid_univers_card_warning'),
          type: 'warning',
        })
        clearErrors('visualNumber')
        return
      }

      setIsChecking(true)
      setFormMessage(null)
      try {
        const response = (await api.assets.getShellToken(
          number.trim()
        )) as ShellTokenResponse
        if (response && response.status === 200) {
          setIsCardValid(true)
          setFormMessage(null)
          clearErrors('visualNumber')
        } else {
          setIsCardValid(false)
          setFormMessage({
            text: t('external_rfid_add_external_not_found'),
            type: 'error',
          })
          clearErrors('visualNumber')
        }
      } catch (error: any) {
        setIsCardValid(false)
        setFormMessage(getTokenErrorInfo(error))
        clearErrors('visualNumber')
      } finally {
        setIsChecking(false)
      }
    },
    [t, clearErrors, getTokenErrorInfo, isUniversCard]
  )

  useEffect(() => {
    // Clear any previous form message when user starts typing
    setFormMessage(null)

    const timeoutId = setTimeout(() => {
      if (visualNumber && visualNumber.trim().length >= 3) {
        checkCardExists(visualNumber)
      } else {
        setIsCardValid(false)
      }
    }, DEBOUNCE_DELAY_MS)
    return () => clearTimeout(timeoutId)
  }, [visualNumber, checkCardExists])

  const handleSubmit = async (): Promise<void> => {
    if (!isCardValid || isSubmitting) {
      return
    }
    setIsSubmitting(true)
    setFormMessage(null)
    try {
      const response = await api.assets.activateShellToken(visualNumber.trim())
      if (response && (response.status === 200 || response.status === 202)) {
        dispatch(
          openToast(TYPE_SUCCESS, t('external_rfid_add_external_success'))
        )
        reset()
        setFormMessage(null)
        eventEmitter.emit('closeModal', { id: 'add-external-rfid' })
        onSuccess()
      } else {
        setFormMessage({
          text: t('external_rfid_add_external_error'),
          type: 'error',
        })
      }
    } catch (error: any) {
      setFormMessage(getTokenErrorInfo(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <TextLevel alignment="left" fontStyleResponsive={['md', 'md', 'md']}>
        {t('external_rfid_add_external_modal_description')}
      </TextLevel>
      <Spacer size={SpacerSizeTypes.XL} />
      <Form onSubmit={handleSubmit} formMethods={formMethods as any}>
        <Input
          id="visualNumber"
          label={`${t('external_rfid_add_external_input_label')}`}
          formMethods={formMethods as any}
          validation={{
            required: t('external_rfid_add_external_validation_required'),
          }}
        />
        <Spacer size={SpacerSizeTypes.XL} />
        <ButtonContainer alignment="center">
          <Button
            type={ButtonType.primary}
            onClick={handleSubmit}
            disabled={!isCardValid || isChecking || isSubmitting}
          >
            {t('external_rfid_add_external_button')}
          </Button>
        </ButtonContainer>
      </Form>
      <MessageBox type={formMessage?.type} message={formMessage?.text} />
    </>
  )
}

export default AddExternalRfidForm
